import { useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import { FileText, Library, Sparkles, Upload } from "lucide-react";
import type { SelectedSkill, SkillDefinition } from "../../api/types.js";
import { recentFiles } from "../shared/constants.js";

export function ComposerMenu({
  fileInputRef,
  menuRef,
  skills,
  setSelectedSkill,
  onClose,
  focusMenuItem,
  moveMenuFocus,
  onSaveLinkAsset,
  onAttachRecent,
  onManageSkills
}: {
  fileInputRef: RefObject<HTMLInputElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  skills: SkillDefinition[];
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkill | null>>;
  onClose: () => void;
  focusMenuItem: (menu: HTMLElement | null, target?: "first" | "last", selector?: string) => void;
  moveMenuFocus: (menu: HTMLElement | null, direction: -1 | 1, selector?: string) => void;
  onSaveLinkAsset: () => void;
  onAttachRecent: (title: string) => void;
  onManageSkills: () => void;
}) {
  const [openSubmenu, setOpenSubmenu] = useState<"recent" | "skills" | null>(null);
  const recentTriggerRef = useRef<HTMLButtonElement>(null);
  const skillsTriggerRef = useRef<HTMLButtonElement>(null);
  const recentMenuRef = useRef<HTMLDivElement>(null);
  const skillsMenuRef = useRef<HTMLDivElement>(null);
  const composerSkills = skills;

  const submenuMenu = (name: "recent" | "skills") => name === "recent" ? recentMenuRef.current : skillsMenuRef.current;
  const submenuTrigger = (name: "recent" | "skills") => name === "recent" ? recentTriggerRef.current : skillsTriggerRef.current;
  const openSubmenuAndFocus = (name: "recent" | "skills") => {
    setOpenSubmenu(name);
    requestAnimationFrame(() => focusMenuItem(submenuMenu(name)));
  };
  const closeSubmenu = (name: "recent" | "skills", restoreFocus = true) => {
    setOpenSubmenu((current) => current === name ? null : current);
    if (restoreFocus) requestAnimationFrame(() => submenuTrigger(name)?.focus());
  };
  const onRootMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveMenuFocus(event.currentTarget, event.key === "ArrowDown" ? 1 : -1, "[data-root-menuitem]");
      return;
    }
    const trigger = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-submenu-trigger]");
    if (!trigger) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      return;
    }
    const name = trigger.dataset.submenuTrigger as "recent" | "skills";
    if (event.key === "ArrowRight" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openSubmenuAndFocus(name);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  };
  const onSubmenuKeyDown = (name: "recent" | "skills") => (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveMenuFocus(event.currentTarget, event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "ArrowLeft" || event.key === "Escape") {
      event.preventDefault();
      closeSubmenu(name);
    }
  };

  return (
    <div ref={menuRef} className="plus-menu" id="composer-plus-menu" role="menu" aria-label="添加上下文" onKeyDown={onRootMenuKeyDown}>
      <button className="plus-menu-item" data-root-menuitem role="menuitem" type="button" onClick={() => fileInputRef.current?.click()}>
        <Upload className="plus-menu-icon" size={16} />
        <span>上传照片和文件</span>
      </button>
      <div
        className="plus-menu-row has-submenu"
        data-submenu-open={openSubmenu === "recent" || undefined}
        onMouseEnter={() => setOpenSubmenu("recent")}
        onMouseLeave={() => setOpenSubmenu((current) => current === "recent" ? null : current)}
      >
        <button
          ref={recentTriggerRef}
          className="plus-menu-item"
          data-root-menuitem
          data-submenu-trigger="recent"
          role="menuitem"
          type="button"
          aria-haspopup="menu"
          aria-expanded={openSubmenu === "recent"}
          aria-controls="composer-recent-menu"
          onClick={() => openSubmenu === "recent" ? closeSubmenu("recent", false) : setOpenSubmenu("recent")}
        >
          <FileText className="plus-menu-icon" size={16} />
          <span>近期文件</span>
          <span className="plus-menu-arrow">›</span>
        </button>
        <div ref={recentMenuRef} id="composer-recent-menu" className="plus-submenu plus-submenu-recent" role="menu" aria-label="近期文件" onKeyDown={onSubmenuKeyDown("recent")}>
          <button className="plus-menu-item" role="menuitem" type="button" onClick={onSaveLinkAsset}>
            <Library className="plus-menu-icon" size={16} />
            <span>从库中添加</span>
          </button>
          <div className="plus-submenu-section">最近</div>
          {recentFiles.map((file) => (
            <button
              className="recent-file-row"
              role="menuitem"
              type="button"
              key={file.title}
              onClick={() => {
                onAttachRecent(file.title);
                onClose();
              }}
            >
              <span className="recent-file-icon">{file.kind}</span>
              <span><strong>{file.title}</strong><small>{file.meta}</small></span>
            </button>
          ))}
        </div>
      </div>
      <div className="plus-menu-divider" />
      <div
        className="plus-menu-row has-submenu"
        data-submenu-open={openSubmenu === "skills" || undefined}
        onMouseEnter={() => setOpenSubmenu("skills")}
        onMouseLeave={() => setOpenSubmenu((current) => current === "skills" ? null : current)}
      >
        <button
          ref={skillsTriggerRef}
          className="plus-menu-item"
          data-root-menuitem
          data-submenu-trigger="skills"
          role="menuitem"
          type="button"
          aria-haspopup="menu"
          aria-expanded={openSubmenu === "skills"}
          aria-controls="composer-skills-menu"
          onClick={() => openSubmenu === "skills" ? closeSubmenu("skills", false) : setOpenSubmenu("skills")}
        >
          <Sparkles className="plus-menu-icon" size={16} />
          <span>使用技能</span>
          <span className="plus-menu-arrow">›</span>
        </button>
        <div ref={skillsMenuRef} id="composer-skills-menu" className="plus-submenu plus-submenu-skills" role="menu" aria-label="使用技能" onKeyDown={onSubmenuKeyDown("skills")}>
          {composerSkills.map((skill) => (
            <button
              className="plus-menu-item"
              role="menuitem"
              type="button"
              key={skill.name}
              onClick={() => {
                setSelectedSkill({
                  name: skill.name,
                  displayName: skill.displayName,
                  packSlug: skill.packSlug,
                  packDisplayName: skill.packDisplayName
                });
                onClose();
              }}
            >
              <span className="plus-skill-copy">
                <strong>{skill.displayName}</strong>
                <small>{skill.packDisplayName}</small>
              </span>
            </button>
          ))}
          <button className="plus-menu-item is-muted" role="menuitem" type="button" onClick={() => { onClose(); onManageSkills(); }}>更多</button>
        </div>
      </div>
    </div>
  );
}
